import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchEncounterByReference } from "../services/encounterService";
import { fetchOrganizationByReference } from "../services/organizationService";
import styles from "../modals/ResourceViewerModal.module.css";
import localStyles from "./SpecimenCardExtended.module.css";
import { formatLocalDate } from "../utils/dateUtils";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import { resolveName } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";

const SectionHeader = ({ children }) => (
    <div className={localStyles.sectionHeader}>{children}</div>
);

const Field = ({ label, children }) => (
    <div className="mb-1">
        <strong>{label}:</strong>{" "}
        {children || <span className="text-muted">N/A</span>}
    </div>
);

const renderCodeable = (cc) => {
    if (!cc) return <span className="text-muted">N/A</span>;

    if (cc.coding?.length) {
        return (
            <>
                {cc.coding.map((c, idx) => (
                    <div key={idx} className="text-muted small ps-3">
                        {c.system ? `${c.system} → ` : ""}
                        {c.code} — {c.display || c.code}
                    </div>
                ))}
            </>
        );
    }

    if (cc.text) return <div>{cc.text}</div>;
    return <span className="text-muted">N/A</span>;
};

const ConditionCardExtended = ({ resource, handleShowNested, fhirClient }) => {
    const store = useStore();
    const [subject, setSubject] = useState(null);
    const [asserter, setAsserter] = useState(null);
    const [recorder, setRecorder] = useState(null);
    const [encounter, setEncounter] = useState(null);
    const [organization, setOrganization] = useState(null);

    /* ================= FETCH REFERENCES ================= */
    useEffect(() => {
        if (!resource || !fhirClient) return;

        const fetchRefs = async () => {
            try {
                if (resource.subject?.reference) {
                    const p = await fetchPatientByReference(
                        resource.subject.reference,
                        fhirClient,
                        store
                    );
                    setSubject(p);
                }

                if (resource.asserter?.reference) {
                    const a = await fetchProviderByReference(
                        resource.asserter.reference,
                        fhirClient,
                        store
                    );
                    setAsserter(a);
                }

                if (resource.recorder?.reference) {
                    const r = await fetchProviderByReference(
                        resource.recorder.reference,
                        fhirClient,
                        store
                    );
                    setRecorder(r);
                }

                if (resource.encounter?.reference) {
                    const e = await fetchEncounterByReference(
                        resource.encounter.reference,
                        fhirClient,
                        store
                    );
                    setEncounter(e);
                }

                if (resource.recorder?.reference?.startsWith("Organization/")) {
                    const o = await fetchOrganizationByReference(
                        resource.recorder.reference,
                        fhirClient,
                        store
                    );
                    setOrganization(o);
                }
            } catch (err) {
                console.error("ConditionCardExtended: failed to fetch references", err);
            }
        };

        fetchRefs();
    }, [resource, fhirClient, store]);

    /* ================= DISPLAY HELPERS ================= */
    const patientName = resolveName(subject, fhirClient, store);
    const patientPhoto =
        subject?.photo?.[0]?.url ||
        (subject?.gender === "female" ? FemalePatient : MalePatient);

    const asserterName = resolveName(asserter, fhirClient, store);
    const asserterPhoto =
        asserter?.photo?.[0]?.url ||
        (asserter?.gender === "female" ? FemaleProvider : MaleProvider);

    const recorderName = resolveName(recorder, fhirClient, store);
    const orgName =
        resolveName(organization, fhirClient, store) ||
        (organization?.id ? `Organization/${organization.id}` : "Unknown Organization");

    /* ================= RENDER ================= */
    return (
        <div className="p-4 border rounded shadow-sm bg-white">
            {/* ========== HEADER PORTRAITS ========== */}
            <div className={localStyles.headerRow}>
                {/* Patient */}
                <div className={localStyles.entityBlock}>
                    <button
                        className={localStyles.entityImageButton}
                        onClick={() => handleShowNested?.(subject, patientName)}
                    >
                        <img src={patientPhoto} alt="Patient" className={localStyles.avatar} />
                    </button>
                    <div>
                        <Field label="Patient">{patientName}</Field>
                        <Field label="Birth Date">{subject?.birthDate}</Field>
                    </div>
                </div>

                {/* Asserter / Recorder */}
                <div className={localStyles.organizationBlock}>
                    <button
                        className={localStyles.entityImageButton}
                        onClick={() => handleShowNested?.(asserter, asserterName)}
                    >
                        <img src={asserterPhoto} alt="Asserter" className={localStyles.avatar} />
                    </button>
                    <div className={localStyles.orgText}>
                        <Field label="Asserter">{asserterName}</Field>
                        <Field label="Recorder">{recorderName}</Field>
                    </div>


                </div>
            </div>

            {/* IDENTIFICATION */}
            <SectionHeader>Identification</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Condition ID">{resource.id}</Field>
                    <Field label="Clinical Status">
                        {renderCodeable(resource.clinicalStatus)}
                    </Field>
                    <Field label="Verification Status">
                        {renderCodeable(resource.verificationStatus)}
                    </Field>
                    <Field label="Category">
                        {resource.category?.map(renderCodeable)}
                    </Field>
                    <Field label="Condition Code">
                        {renderCodeable(resource.code)}
                    </Field>
                    <Field label="Severity">
                        {renderCodeable(resource.severity)}
                    </Field>
                    <Field label="Body Site">
                        {resource.bodySite?.map(renderCodeable)}
                    </Field>
                </div></div>

            {/* TIMING */}
            <SectionHeader>Timing</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Onset">
                        {formatLocalDate(
                            resource.onsetDateTime ||
                            resource.onsetPeriod?.start ||
                            resource.onsetAge?.value
                        )}
                    </Field>
                    <Field label="Abatement">
                        {formatLocalDate(
                            resource.abatementDateTime ||
                            resource.abatementPeriod?.end
                        )}
                    </Field>
                    <Field label="Recorded Date">
                        {formatLocalDate(resource.recordedDate)}
                    </Field>
                </div></div>

            {/* CONTEXT */}
            <SectionHeader>Context</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Encounter">
                        {resource.encounter?.reference
                            ? renderReference(
                                resource.encounter.reference,
                                resolveName(encounter, fhirClient, store) || encounter?.id,
                                handleShowNested
                              )
                            : <span className="text-muted">N/A</span>
                        }
                    </Field>
                    <Field label="Stage">
                        {resource.stage?.summary?.text ||
                            renderCodeable(resource.stage?.summary)}
                    </Field>
                    <Field label="Evidence">
                        {resource.evidence
                            ?.map((e) => e.code?.text || e.detail?.[0]?.display)
                            .join(", ")}
                    </Field>
                </div></div>

            {/* NOTES */}
            {resource.note?.length > 0 && (
                <>
                    <SectionHeader>Notes</SectionHeader>
                    <div className={styles.indented}>
                        {resource.note.map((n, idx) => (
                            <div key={idx} className="mb-1">
                                {n.text}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* METADATA */}
            <SectionHeader>Metadata</SectionHeader>
            <div className={styles.indented}>
                <Field label="FHIR Resource Type">{resource.resourceType}</Field>
                <Field label="Meta Version">{resource.meta?.versionId}</Field>
                <Field label="Last Updated">
                    {resource.meta?.lastUpdated
                        ? formatLocalDate(resource.meta.lastUpdated)
                        : "N/A"}
                </Field>
            </div>
        </div>
    );
};

export default ConditionCardExtended;
